import { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View, type StyleProp, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, control, fontFamily, radii, space, spacing, type } from '~/ui/tokens';
import { ModalSheet } from './ModalSheet';
import { CountryFlag } from './CountryFlag';

export interface SelectOption {
  value: string;
  label: string;
  /** Extra searchable text not shown on the trigger (e.g. a dial code). */
  hint?: string;
}

interface SelectProps {
  value: string | null;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  /** Overrides the trigger text — e.g. show just "+91" for a country code
   * while the option list still reads "🇮🇳 India (+91)". */
  displayValue?: string;
  searchable?: boolean;
  title?: string;
  style?: StyleProp<ViewStyle>;
  /** Treats each option's `value` as an ISO 3166-1 alpha-2 code and shows its
   * flag before the label, on the trigger and in every row — used for the
   * phone country-code and residency/country pickers so both share one
   * consistent look instead of one having a flag and the other not. */
  flags?: boolean;
}

export function Select({
  value,
  onChange,
  options,
  label,
  placeholder = 'Select',
  displayValue,
  searchable = false,
  title,
  style,
  flags = false,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.value === value) ?? null;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint?.toLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  const hasValue = Boolean(displayValue || selected);
  const triggerText = displayValue ?? selected?.label ?? placeholder;

  return (
    <View style={style}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label ?? placeholder}
        onPress={() => {
          setQuery('');
          setOpen(true);
        }}
        style={styles.trigger}
      >
        <View style={styles.triggerContent}>
          {flags && value ? <CountryFlag code={value} size={18} /> : null}
          <Text style={[styles.triggerText, !hasValue && styles.placeholder]} numberOfLines={1}>
            {triggerText}
          </Text>
        </View>
        <Ionicons name="chevron-down" size={18} color={colors.inkFaint} />
      </Pressable>

      <ModalSheet visible={open} onClose={() => setOpen(false)}>
        <View style={styles.sheet}>
          <View style={styles.sheetHeader}>
            <Text style={styles.sheetTitle}>{title ?? label ?? 'Select'}</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={() => setOpen(false)}>
              <Ionicons name="close" size={22} color={colors.inkSoft} />
            </Pressable>
          </View>

          {searchable ? (
            <View style={styles.searchBox}>
              <Ionicons name="search" size={16} color={colors.inkFaint} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search"
                placeholderTextColor={colors.inkFaint}
                style={styles.searchInput}
              />
            </View>
          ) : null}

          <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
            {filtered.map((opt) => {
              const active = opt.value === value;
              return (
                <Pressable
                  key={opt.value}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  onPress={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={[styles.option, active && styles.optionActive]}
                >
                  <View style={styles.optionContent}>
                    {flags ? <CountryFlag code={opt.value} size={20} /> : null}
                    <Text style={[styles.optionText, active && styles.optionTextActive]} numberOfLines={1}>
                      {opt.label}
                    </Text>
                  </View>
                  {active ? <Ionicons name="checkmark" size={18} color={colors.brand} /> : null}
                </Pressable>
              );
            })}
            {filtered.length === 0 ? <Text style={styles.empty}>No matches</Text> : null}
          </ScrollView>
        </View>
      </ModalSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  label: {
    fontFamily: fontFamily.medium,
    ...control.inputLabel,
    color: colors.inkSoft,
    marginBottom: space.related,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(1),
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    paddingHorizontal: spacing(1.75),
    height: 48,
    backgroundColor: colors.card,
  },
  triggerContent: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(0.75),
    minWidth: 0,
  },
  triggerText: {
    flexShrink: 1,
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
  },
  placeholder: {
    color: colors.inkFaint,
  },
  sheet: {
    padding: spacing(2),
    gap: spacing(1.5),
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sheetTitle: {
    fontFamily: fontFamily.semibold,
    ...type.cardTitle,
    color: colors.ink,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    paddingHorizontal: spacing(1.5),
    height: 44,
  },
  searchInput: {
    flex: 1,
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
    height: '100%',
    ...({ outlineStyle: 'none' } as object),
  },
  list: {
    maxHeight: 360,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing(1.5),
    paddingHorizontal: spacing(1),
    borderRadius: radii.sm,
  },
  optionActive: {
    backgroundColor: colors.skyTint,
  },
  optionContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    minWidth: 0,
  },
  optionText: {
    flexShrink: 1,
    fontFamily: fontFamily.regular,
    ...type.bodyLarge,
    color: colors.ink,
  },
  optionTextActive: {
    fontFamily: fontFamily.semibold,
    color: colors.brand,
  },
  empty: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkFaint,
    paddingVertical: space.internal,
    textAlign: 'center',
  },
});
